import { Alert } from "@xola/ui-kit";
import { FormField, SwitchField } from "common";
import { useFormikContext } from "formik";
import { isNil } from "lodash";
import React from "react";
import { useHistory } from "react-router-dom";
import { CheckInValidationType, Experience } from "types";
import { MultipleCheckInField } from "../ExperienceCheckIn/MultipleCheckInField";
import { GuestManagementFormValues } from "./GuestManagement";
import { getCheckInPreferenceValues } from "./helpers";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { CheckInWindowField } from "@/components/Product/ExperienceCheckIn/CheckInWindow/CheckInWindowField";
import { filterCheckInWindowValidations } from "@/components/Product/ExperienceCheckIn/CheckInWindow/PurchaseCutoffScheduleInput";
import { FirstCheckInCutoffField } from "@/components/Product/ExperienceCheckIn/FirstCheckInCutoffField";
import { hasCountValidation } from "@/components/Product/ExperienceCheckIn/useMultiCheckInFieldValues";
import { useSeller } from "@/hooks/useAccount";

type Props = {
    experience: Experience;
};

export const ExperienceCheckInSection = ({ experience }: Props) => {
    const history = useHistory();
    const seller = useSeller();
    const globalCheckInPreference = seller.preferences.checkIn;

    const {
        values,
        setFieldValue: setFormikFieldValue,
        setFieldTouched,
        touched,
    } = useFormikContext<GuestManagementFormValues>();

    const isOverridingGlobalPreference = !isNil(values.checkInPreference);
    const globalPreference = seller.preferences.checkIn;

    const setFieldValue = (name: string, value: any) => {
        setFormikFieldValue(name, value);
        setFieldTouched(name);
    };

    const enabledValue = isOverridingGlobalPreference
        ? values.checkInPreference?.enabled ?? false
        : globalCheckInPreference.enabled;

    const isCheckInEnabled = values.checkInPreference?.enabled ?? globalCheckInPreference.enabled;

    const handleGlobalOverride = (value: boolean) => {
        if (!value) {
            setFieldValue("checkInPreference", null);
            return;
        }

        const experienceValidations =
            experience.checkInPreference?.validations.filter(
                (validation) => validation.object !== CheckInValidationType.COUNT,
            ) ?? [];

        setFieldValue("checkInPreference", {
            enabled: globalCheckInPreference.enabled,
            validations: [...experienceValidations, { object: CheckInValidationType.COUNT, max: 1 }],
            multiCheckInEnabled: false,
            checkInWindowEnabled: filterCheckInWindowValidations(experienceValidations).length > 0,
        });
    };

    const handleCheckInToggle = (value: boolean) => {
        if (
            value &&
            !hasCountValidation(values.checkInPreference?.validations) &&
            !(touched.checkInPreference as any)?.validations
        ) {
            setFieldValue("checkInPreference.validations", [{ object: CheckInValidationType.COUNT, max: 1 }]);
        }

        setFieldValue("checkInPreference.enabled", value);
    };

    const redirectToGlobalCheckInSettings = () => {
        history.push("/products/globalSettings/checkin");
    };

    return (
        <>
            <ContentLayout.Section title="Check-In" description="Configure your guest check-in workflow">
                <div className="space-y-4">
                    <Alert>
                        <div className="flex flex-col space-y-1">
                            <SwitchField isChecked={isOverridingGlobalPreference} onChange={handleGlobalOverride}>
                                <span className="font-bold text-black">Override global check-in settings</span>
                            </SwitchField>
                            <span>
                                This experience is {isOverridingGlobalPreference ? "not" : ""} using the{" "}
                                <a className="cursor-pointer font-bold" onClick={redirectToGlobalCheckInSettings}>
                                    Check-In Settings
                                </a>
                                {isOverridingGlobalPreference
                                    ? ". It is using the settings configured below"
                                    : ". You can set custom check-in preferences specific to this experience by overriding the settings below."}
                            </span>
                        </div>
                    </Alert>

                    <FormField name="enabled" label="Require Check-In">
                        <SwitchField
                            isDisabled={!isOverridingGlobalPreference}
                            isChecked={enabledValue}
                            onChange={handleCheckInToggle}
                        >
                            Enable guest check-ins for this product
                        </SwitchField>
                    </FormField>

                    {isCheckInEnabled && (
                        <FormField name="enabled" label="Check-In Validity">
                            <div className="flex flex-col space-y-1">
                                <CheckInWindowField
                                    disabled={!isOverridingGlobalPreference || !values.requireGuestIdentification}
                                    requireGuestIdentification={values.requireGuestIdentification}
                                    value={values.checkInPreference?.validations ?? []}
                                />
                                <FirstCheckInCutoffField
                                    disabled={!isOverridingGlobalPreference || !values.requireGuestIdentification}
                                    requireGuestIdentification={values.requireGuestIdentification}
                                    values={values.checkInPreference ?? null}
                                />
                            </div>
                        </FormField>
                    )}

                    {isCheckInEnabled && (
                        <MultipleCheckInField
                            namePrefix="checkInPreference."
                            disabled={!isOverridingGlobalPreference || experience.earlyReturn}
                            tooltip={
                                isOverridingGlobalPreference
                                    ? experience.earlyReturn
                                        ? "Multiple check-ins cannot be enabled as this experience has early returns configured"
                                        : undefined
                                    : "Using configurations from global Check-in settings"
                            }
                            values={
                                isOverridingGlobalPreference
                                    ? values.checkInPreference ?? null
                                    : getCheckInPreferenceValues(globalPreference)
                            }
                        />
                    )}
                </div>
            </ContentLayout.Section>

            <hr className="my-6 border-gray-light" />
        </>
    );
};
